import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  AUTH_LOGOUT_EVENT,
  TOKENS_UPDATED_EVENT,
  authStorage,
  subscribeToAuthSync,
} from "@/lib/auth-storage";
import {
  acceptInvite as acceptInviteRequest,
  getCurrentUser,
  loginAdmin as loginAdminRequest,
  loginDozent as loginDozentRequest,
  logout as logoutRequest,
} from "@/lib/api/auth";
import { getJwtExpiryMs } from "@/lib/jwt";
import {
  isAuthRefreshUnavailableError,
  logoutAndRedirect,
  refreshTokens,
} from "@/lib/http";
import type { AcceptInviteResponse, AuthResponse } from "@/lib/api/auth";
import type { StoredTokens } from "@/lib/auth-storage";
import {
  verifyMfaLogin,
  verifyMfaPasskeyLogin,
} from "@/features/saas-mfa/api";
import { ADMIN_INACTIVITY_TIMEOUT_MS } from "@/lib/inactivity";
import { AuthContext, type AuthContextValue } from "./auth-context";

const REFRESH_SKEW_MS = 60 * 1000;
const AUTO_LOGOUT_SKEW_MS = 30 * 1000;
const REFRESH_RETRY_DELAY_MS = 30 * 1000;
const ADMIN_REFRESH_INTERVAL_MS = ADMIN_INACTIVITY_TIMEOUT_MS - REFRESH_SKEW_MS;
const SESSION_REFRESH_WARNING = "Bitte die Seite neu laden.";

type AdminRefreshBlockReason = "hidden" | "idle" | null;

const isAdminRoute = () =>
  typeof window !== "undefined" &&
  (window.location.pathname === "/admin" ||
    window.location.pathname.startsWith("/admin/"));

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<AuthContextValue["user"]>(null);
  const [loading, setLoading] = useState(true);
  const [sessionRefreshWarning, setSessionRefreshWarning] = useState<
    string | null
  >(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const retryRefreshSessionRef = useRef<() => void>(() => undefined);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentUserRoleRef = useRef<string | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const clearRefreshRetryTimer = useCallback(() => {
    if (refreshRetryTimerRef.current) {
      clearTimeout(refreshRetryTimerRef.current);
      refreshRetryTimerRef.current = null;
    }
  }, []);

  const clearLogoutTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const clearSessionState = useCallback(() => {
    currentUserRoleRef.current = null;
    setUser(null);
    setSessionRefreshWarning(null);
    clearRefreshTimer();
    clearRefreshRetryTimer();
    clearLogoutTimer();
  }, [clearLogoutTimer, clearRefreshRetryTimer, clearRefreshTimer]);

  const updateAdminBrowserSession = useCallback((role: string) => {
    currentUserRoleRef.current = role;
    if (role === "ADMIN") {
      authStorage.markAdminSession();
      return;
    }
    authStorage.clearAdminSessionMarker();
  }, []);

  const resolveAdminRefreshBlockReason =
    useCallback((): AdminRefreshBlockReason => {
      const isAdminSession =
        currentUserRoleRef.current === "ADMIN" ||
        authStorage.hasAdminSessionMarker();
      if (!isAdminSession) return null;
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        return "hidden";
      }
      const lastActivityAt = authStorage.getAdminLastActivityAt();
      if (
        !lastActivityAt ||
        Date.now() - lastActivityAt >= ADMIN_INACTIVITY_TIMEOUT_MS
      ) {
        return "idle";
      }
      return null;
    }, []);

  const resolveAccessTokenExpiryMs = useCallback(
    (tokens: StoredTokens | null) => {
      if (!tokens?.accessToken) return null;
      if (typeof tokens.accessTokenExpiresAtMs === "number") {
        return tokens.accessTokenExpiresAtMs;
      }
      if (tokens.accessTokenExpiresAt) {
        const parsed = Date.parse(tokens.accessTokenExpiresAt);
        if (!Number.isNaN(parsed)) {
          return parsed;
        }
      }
      return getJwtExpiryMs(tokens.accessToken);
    },
    [],
  );

  const scheduleAutoLogout = useCallback(() => {
    clearLogoutTimer();
    const tokens = authStorage.get();
    const expiryMs = resolveAccessTokenExpiryMs(tokens);
    if (!expiryMs) return;
    const delay = Math.max(expiryMs - AUTO_LOGOUT_SKEW_MS - Date.now(), 0);
    logoutTimerRef.current = setTimeout(() => {
      logoutAndRedirect();
    }, delay);
  }, [clearLogoutTimer, resolveAccessTokenExpiryMs]);

  const scheduleRefreshRetry = useCallback(() => {
    clearRefreshRetryTimer();
    refreshRetryTimerRef.current = setTimeout(() => {
      const blockReason = resolveAdminRefreshBlockReason();
      if (blockReason === "idle") {
        logoutAndRedirect();
        return;
      }
      if (blockReason === "hidden") return;
      retryRefreshSessionRef.current();
    }, REFRESH_RETRY_DELAY_MS);
  }, [clearRefreshRetryTimer, resolveAdminRefreshBlockReason]);

  const handleRefreshUnavailable = useCallback(() => {
    setSessionRefreshWarning(SESSION_REFRESH_WARNING);
    scheduleRefreshRetry();
  }, [scheduleRefreshRetry]);

  const scheduleTokenRefresh = useCallback(() => {
    clearRefreshTimer();
    const tokens = authStorage.get();
    const expiryMs = resolveAccessTokenExpiryMs(tokens);
    if (!expiryMs) return;
    const accessTokenDelay = Math.max(
      expiryMs - REFRESH_SKEW_MS - Date.now(),
      0,
    );
    const isAdminSession =
      currentUserRoleRef.current === "ADMIN" ||
      authStorage.hasAdminSessionMarker();
    const delay = isAdminSession
      ? Math.min(accessTokenDelay, ADMIN_REFRESH_INTERVAL_MS)
      : accessTokenDelay;
    refreshTimerRef.current = setTimeout(() => {
      void (async () => {
        const blockReason = resolveAdminRefreshBlockReason();
        if (blockReason === "idle") {
          logoutAndRedirect();
          return;
        }
        if (blockReason === "hidden") return;
        try {
          const refreshed = await refreshTokens();
          if (!refreshed?.accessToken) {
            logoutAndRedirect();
            return;
          }
          setSessionRefreshWarning(null);
        } catch (error) {
          if (isAuthRefreshUnavailableError(error)) {
            handleRefreshUnavailable();
            return;
          }
          logoutAndRedirect();
          return;
        }
        scheduleTokenRefresh();
        scheduleAutoLogout();
      })();
    }, delay);
  }, [
    clearRefreshTimer,
    handleRefreshUnavailable,
    resolveAccessTokenExpiryMs,
    resolveAdminRefreshBlockReason,
    scheduleAutoLogout,
  ]);

  const retryRefreshSession = useCallback(() => {
    void (async () => {
      const blockReason = resolveAdminRefreshBlockReason();
      if (blockReason === "idle") {
        logoutAndRedirect();
        return;
      }
      if (blockReason === "hidden") return;
      try {
        const refreshed = await refreshTokens();
        if (!refreshed?.accessToken) {
          if (user) {
            logoutAndRedirect();
          } else {
            clearSessionState();
          }
          return;
        }

        const currentUser = await getCurrentUser();
        updateAdminBrowserSession(currentUser.role);
        setUser(currentUser);
        setSessionRefreshWarning(null);
        scheduleTokenRefresh();
        scheduleAutoLogout();
      } catch (error) {
        if (isAuthRefreshUnavailableError(error)) {
          handleRefreshUnavailable();
          return;
        }
        authStorage.clear({ broadcast: false, emitLogout: false });
        clearSessionState();
      }
    })();
  }, [
    clearSessionState,
    handleRefreshUnavailable,
    resolveAdminRefreshBlockReason,
    scheduleAutoLogout,
    scheduleTokenRefresh,
    updateAdminBrowserSession,
    user,
  ]);

  useEffect(() => {
    retryRefreshSessionRef.current = retryRefreshSession;
  }, [retryRefreshSession]);

  const restoreUserSession = useCallback(async () => {
    let tokens = authStorage.get();
    if (isAdminRoute() && !authStorage.hasAdminSessionMarker()) {
      authStorage.clear({ broadcast: false, emitLogout: false });
      clearSessionState();
      return false;
    }
    const blockReason = resolveAdminRefreshBlockReason();
    if (blockReason === "idle") {
      authStorage.clear({ broadcast: false, emitLogout: false });
      clearSessionState();
      return false;
    }
    if (blockReason === "hidden") return false;
    if (!tokens?.accessToken) {
      try {
        tokens = await refreshTokens();
      } catch (error) {
        if (isAuthRefreshUnavailableError(error)) {
          scheduleRefreshRetry();
          return false;
        }
        authStorage.clear({ broadcast: false, emitLogout: false });
        clearSessionState();
        return false;
      }
    }

    if (!tokens?.accessToken) {
      authStorage.clear({ broadcast: false, emitLogout: false });
      clearSessionState();
      return false;
    }

    try {
      const currentUser = await getCurrentUser();
      updateAdminBrowserSession(currentUser.role);
      setUser(currentUser);
      setSessionRefreshWarning(null);
      scheduleTokenRefresh();
      scheduleAutoLogout();
      return true;
    } catch (_error) {
      authStorage.clear({ broadcast: false, emitLogout: false });
      clearSessionState();
      return false;
    }
  }, [
    clearSessionState,
    resolveAdminRefreshBlockReason,
    scheduleAutoLogout,
    scheduleRefreshRetry,
    scheduleTokenRefresh,
    updateAdminBrowserSession,
  ]);

  useEffect(() => {
    const handleLogout = () => {
      authStorage.clear({ broadcast: false, emitLogout: false });
      clearSessionState();
    };

    const handleTokensUpdated = () => {
      void restoreUserSession();
    };

    const unsubscribe = subscribeToAuthSync((type) => {
      if (type === "logout") {
        handleLogout();
        return;
      }
      void restoreUserSession();
    });

    if (typeof window !== "undefined") {
      window.addEventListener(AUTH_LOGOUT_EVENT, handleLogout);
      window.addEventListener(TOKENS_UPDATED_EVENT, handleTokensUpdated);
    }

    return () => {
      unsubscribe();
      if (typeof window !== "undefined") {
        window.removeEventListener(AUTH_LOGOUT_EVENT, handleLogout);
        window.removeEventListener(TOKENS_UPDATED_EVENT, handleTokensUpdated);
      }
      clearRefreshTimer();
      clearRefreshRetryTimer();
      clearLogoutTimer();
    };
  }, [
    clearLogoutTimer,
    clearRefreshRetryTimer,
    clearRefreshTimer,
    clearSessionState,
    restoreUserSession,
  ]);

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      const blockReason = resolveAdminRefreshBlockReason();
      if (blockReason === "idle") {
        logoutAndRedirect();
        return;
      }
      if (blockReason === "hidden") return;
      const tokens = authStorage.get();
      const expiryMs = resolveAccessTokenExpiryMs(tokens);
      if (!expiryMs) {
        return;
      }

      const remaining = expiryMs - Date.now();
      if (remaining <= AUTO_LOGOUT_SKEW_MS) {
        logoutAndRedirect();
        return;
      }

      if (remaining <= REFRESH_SKEW_MS) {
        void (async () => {
          try {
            const refreshed = await refreshTokens();
            if (!refreshed?.accessToken) {
              logoutAndRedirect();
              return;
            }
            setSessionRefreshWarning(null);
          } catch (error) {
            if (isAuthRefreshUnavailableError(error)) {
              handleRefreshUnavailable();
              return;
            }
            logoutAndRedirect();
            return;
          }
          scheduleTokenRefresh();
          scheduleAutoLogout();
        })();
        return;
      }

      scheduleTokenRefresh();
      scheduleAutoLogout();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleVisibilityOrFocus();
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("focus", handleVisibilityOrFocus);
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", handleVisibilityOrFocus);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      }
    };
  }, [
    handleRefreshUnavailable,
    resolveAccessTokenExpiryMs,
    resolveAdminRefreshBlockReason,
    scheduleAutoLogout,
    scheduleTokenRefresh,
  ]);

  useEffect(() => {
    void restoreUserSession().finally(() => setLoading(false));
  }, [restoreUserSession]);

  useEffect(() => {
    const recordAdminActivity = () => {
      if (currentUserRoleRef.current === "ADMIN") {
        authStorage.recordAdminActivity();
      }
    };
    const activityEvents: Array<keyof WindowEventMap> = [
      "mousemove",
      "keydown",
      "touchstart",
    ];
    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, recordAdminActivity),
    );
    return () => {
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, recordAdminActivity),
      );
    };
  }, []);

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id });
      Sentry.setTag("role", user.role);
    } else {
      Sentry.setUser(null);
      Sentry.setTag("role", "anonymous");
    }
  }, [user]);

  const handleAuthSuccess = (response: AuthResponse) => {
    updateAdminBrowserSession(response.user.role);
    authStorage.save(response.tokens);
    setUser(response.user);
    setSessionRefreshWarning(null);
    clearRefreshRetryTimer();
    scheduleTokenRefresh();
    scheduleAutoLogout();
    return response;
  };

  const login = async (
    email: string,
    password: string,
    captchaToken?: string,
  ) => {
    const response = await loginDozentRequest(email, password, captchaToken);
    if (
      response.status === "AUTHENTICATED" ||
      response.status === "MFA_GRACE_AUTHENTICATED"
    ) {
      handleAuthSuccess(response);
    }
    return response;
  };

  const loginAdmin = async (
    email: string,
    password: string,
    captchaToken?: string,
  ) => {
    const response = await loginAdminRequest(email, password, captchaToken);
    if (
      response.status === "AUTHENTICATED" ||
      response.status === "MFA_GRACE_AUTHENTICATED"
    ) {
      handleAuthSuccess(response);
    }
    return response;
  };

  const completeMfaLogin: AuthContextValue["completeMfaLogin"] = async (
    input,
  ) => handleAuthSuccess(await verifyMfaLogin(input));

  const completePasskeyLogin: AuthContextValue["completePasskeyLogin"] =
    async (input) => handleAuthSuccess(await verifyMfaPasskeyLogin(input));

  const handleAcceptInvite = async (input: {
    token: string;
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }): Promise<AcceptInviteResponse> => {
    const response = await acceptInviteRequest(input);
    if (response.status === "COMPLETED") {
      const auth = handleAuthSuccess(response);
      return { status: "COMPLETED", ...auth } as AcceptInviteResponse;
    }
    return response;
  };

  const logout = async () => {
    try {
      await logoutRequest();
    } catch (error) {
      console.warn("Logout request failed", error);
    } finally {
      authStorage.clear();
      clearSessionState();
    }
  };

  const setTokens = (tokens: StoredTokens) => {
    authStorage.save(tokens);
    setSessionRefreshWarning(null);
    clearRefreshRetryTimer();
    scheduleTokenRefresh();
    scheduleAutoLogout();
  };

  const refreshUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setSessionRefreshWarning(null);
    } catch (error) {
      authStorage.clear();
      clearSessionState();
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        sessionRefreshWarning,
        login,
        loginAdmin,
        completeMfaLogin,
        completePasskeyLogin,
        logout,
        acceptInvite: handleAcceptInvite,
        setTokens,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
